import { inject, Injectable } from '@angular/core'
import { FeathersService } from './feathers.service'
import { WorkerAvailability } from '../models/workerAvailability'
import { RecommendedAssistantsFilter } from '../features/assistance/assistance-editor/recommended-assistants-filter'
import { map, switchMap, tap } from 'rxjs/operators'
import { combineLatest, Observable } from 'rxjs'
import { Assistant } from '../models/assistant'
import { Worker } from '../models/worker'
import { DateRange, DateUtils } from './date-utils.service'
import { AssistantService } from './domain/assistant/assistant.service'
import {
  WorkerAvailabilitySelectItem,
  WorkerAvailabilitySelectItemFlag,
} from '../features/worker/worker-select/components/worker-select/WorkerAvailabilitySelectItem'
import { subDays } from 'date-fns'
import {
  WorkerAvailabilitiesOccupationService,
} from './domain/worker-availabilities/worker-availabilities-occupation.service'
import { ExchangeService } from './exchange.service'
import { WorkerStatus } from '../models/worker-status'

@Injectable({
  providedIn: 'root',
})
export class WorkerAvailabilitiesRecommendationService {
  private feathers = inject(FeathersService)
  private assistantService = inject(AssistantService)
  private aaOccupationService = inject(WorkerAvailabilitiesOccupationService)
  private exchangeService = inject(ExchangeService)

  private TAG = 'WorkerAvailabilitiesRecommendationService'

  public getAvailabilitiesInRange(range: DateRange, assistantsToDisable: Assistant[] = []) {
    const assistantIdsToDisable = assistantsToDisable.map((a) => a.id)

    const query = {
      queryType: 'assistanceWorkerSelect',
      ...(range.from && { dt_start: { $lte: range.from } }),
      ...(range.to && {
        $or: [
          { dt_end: { $gte: range.to } },
          {
            rrule: { $ne: null },
            recurrenceEnd: { $gte: subDays(range.to, 1) },
          },
          { rrule: { $ne: null }, recurrenceEnd: null },
        ],
      }),
    }

    const availabilities$ = this.feathers.find(WorkerAvailability, query).pipe(
      map((aa: WorkerAvailability[]) => WorkerAvailability.expandAvailabilitiesWhichEngulfRange(aa, range)),
      map((aa) => aa.filter((a) => !assistantIdsToDisable.includes(a.worker.assistant.id))),
      switchMap((expandedAA) => this.aaOccupationService.addOccupationsAndVacantTimesMulti(expandedAA)),
      // map(aaWithOccupation => aaWithOccupation.filter(a => !a.isOccupiedAt(range))),
    )
    const exchangeAvailabilities$ = this.getActiveExchangeAvailabilitiesEmbracingRange(range).pipe(
      map((aa) => aa.filter((a) => !assistantIdsToDisable.includes(a.worker.assistant.id))),
    )

    return combineLatest([availabilities$, exchangeAvailabilities$]).pipe(
      map(([availabilities, exchangeAvailabilities]) => {
        const availabilitySelectItems = availabilities
          .filter(
            (aa) =>
              // Exclude availabilities that already contain the same worker in exchange availabilities
              !exchangeAvailabilities.find((ea) => ea.worker.assistant.id === aa.worker.assistant.id),
          )
          .map(
            (availability) =>
              new WorkerAvailabilitySelectItem({
                availability,
                enabled: true,
              }),
          )
        const exchangeAvailabilitySelectItems = exchangeAvailabilities
          .filter(
            (ea) =>
              // If there are more exchange availabilities with the same assistant, leave only the first one.
              exchangeAvailabilities.find((ea2) => ea2.worker.assistant.id === ea.worker.assistant.id) === ea,
          )
          .map(
            (availability) =>
              new WorkerAvailabilitySelectItem({
                availability,
                enabled: true,
                flags: [WorkerAvailabilitySelectItemFlag.IsExchangeAssistance],
              }),
          )
        return [...availabilitySelectItems, ...exchangeAvailabilitySelectItems]
      }),
      tap((e) => console.log(`${this.TAG}#getAvailabilitiesInRange`, e)),
    )
  }

  async filterItems(filter: RecommendedAssistantsFilter, items: WorkerAvailabilitySelectItem[], workerStatuses: WorkerStatus[]) {
    return items.filter((i) => {
      return (
        this.filterAvailabilityByAssistanceParameters(filter, i.availability) &&
        this.filterAvailabilityIfOccupied(filter, i.availability) &&
        this.filterAvailabilityIfOverlapsLeaves(i.availability, workerStatuses)
      )
    })
  }

  private filterAvailabilityByAssistanceParameters(
    filter: RecommendedAssistantsFilter,
    availability: WorkerAvailability,
  ) {
    // console.log(availability)
    const deactivatedAssistantCheck = !availability.worker.isInactive()

    const strongCheck = !filter.values.needsStrongAssistant || availability.worker.isStrong
    const advancedCheck = !filter.values.needsAdvancedAssistant || availability.worker.isAdvanced
    const genderCheck =
      !filter.values.preferredGenderId || filter.values.preferredGenderId === availability.worker.user.genderId

    // Tests for date selection
    //
    // Worker select reqs:
    //
    // Data:
    //
    // Non-recurring Availability
    // (1) 3.1. 10:00 - 3.1. 15:00  / 6.2.
    //
    // Recurring Availability
    // (2a) 5.1. 10:00 - 5.1. 12:00 / 7.2.
    // (2b) 6.1. 10:00 - 6.1. 12:00
    // (2c) 7.1. 10:00 - 7.1. 12:00
    //
    //
    // Test cases:
    //
    // TimeFrom: 3.1. 10:00
    // TimeTo: 3.1. 14:00
    // Expected result: (1)
    //
    // TimeFrom: 5.1. 10:00
    // TimeTo: 5.1. 11:00
    // Expected result: (2a)
    //
    // TimeFrom: 5.1. 10:00
    // TimeTo: 5.1. 14:00
    // Expected result: (---)
    //
    // TimeFrom: 5.1. 11:00
    // TimeTo: 5.1. 14:00
    // Expected result: (---)
    //
    // TimeFrom: 1.1. 10:00
    // TimeTo: ---
    // Expected result: (1, 2a, 2b, 2c)
    // Reason: Whichever Worker is available on TimeFrom and ends later, ie. has Assistant.timeFrom <= filter.TimeFrom && Assistant.TimeTo > filter.TimeFrom
    //
    // TimeFrom: ---
    // TimeTo: 4.1. 10:00
    // Expected result: ()
    // Reason: Whichever Worker is available on TimeTo and starts earlier, ie. has Assistant.timeTo >= filter.TimeTo && Assistant.TimeFrom < filter.TimeTo

    // check na datum jeste znova kvuli rekurencim
    let dateFromCheck: boolean = true
    let dateToCheck: boolean = true

    const assistantStart: Date = availability.dt_start ? availability.getDateFrom() : null
    const assistantEnd: Date = availability.dt_end ? availability.getDateTo() : null
    const filterStart: Date = new Date(filter.values.dateFrom)
    const filterEnd: Date = new Date(filter.values.dateTo)

    if (assistantStart && assistantEnd) {
      if (filter.values.dateFrom && filter.values.dateTo) {
        dateFromCheck = assistantStart <= filterStart
        dateToCheck = assistantEnd >= filterEnd
      } else if (filter.values.dateFrom) {
        dateFromCheck = assistantStart <= filterStart && assistantEnd > filterStart
      } else if (filter.values.dateTo) {
        dateToCheck = assistantEnd >= filterEnd && assistantStart < filterEnd
      }
    }

    const dateCheck = dateFromCheck && dateToCheck

    return strongCheck && advancedCheck && genderCheck && dateCheck && deactivatedAssistantCheck
  }

  private filterAvailabilityIfOccupied(filter: RecommendedAssistantsFilter, availability: WorkerAvailability) {
    if (!availability.assistanceOccupations) {
      // console.log(this.TAG, 'filterAvailabilityIfOccupied no assistanceOccupations', availability)
      return true
    }
    // if availability is occupied on the given date, filter it out
    const overlap = DateUtils.dateRangesAnyOverlap([filter.getDateRange()], availability.getOccupiedTimes())
    // console.log(this.TAG, 'should filter out occupied availability', overlap)
    return !overlap
  }

  private filterAvailabilityIfOverlapsLeaves(availability: WorkerAvailability, workerStatuses: WorkerStatus[]) {
    const workerStatus = workerStatuses.find(ws => ws.id === +availability.workerId)
    if (workerStatus) {
      return !workerStatus.hasOverlapWithLeave
    }
    return true
  }

  getAvailabilitiesForAllActiveAssistants(
    range: DateRange,
    assistantsToDisable: Assistant[],
  ): Observable<WorkerAvailabilitySelectItem[]> {
    return this.assistantService.getAllActiveWithUserWithCaching().pipe(
      map((activeAssistants) => {
        return activeAssistants.map((activeAssistant) => {
          const availability = new WorkerAvailability()
          const worker = Worker.createFromProps(Worker, {
            user: activeAssistant.worker.user,
            assistant: activeAssistant,
            workerAllocations: activeAssistant.worker.workerAllocations,
          })
          worker.assistant.id = activeAssistant.id
          availability.worker = worker

          const disabled = assistantsToDisable.some((aToDisable) => activeAssistant.id === aToDisable.id)

          const item = new WorkerAvailabilitySelectItem({
            availability,
            enabled: !disabled,
          })

          return item
        })
      }),
    )
  }

  getActiveExchangeAvailabilitiesEmbracingRange(range: DateRange): Observable<WorkerAvailability[]> {
    return this.exchangeService.getActiveEnrollmentsEmbracingRange(range).pipe(
      tap((enrollments) => {
        console.log(this.TAG, 'getActiveExchangeAvailabilitiesEmbracingRange got enrollments', enrollments)
      }),
      map((enrollments) =>
        enrollments.map((enrollment) => {
          const availability = new WorkerAvailability()
          const worker = new Worker()
          worker.assistant = enrollment.assistant
          worker.assistant.id = enrollment.assistant.id
          availability.worker = worker
          return availability
        }),
      ),
    )
  }
}
